import axios from 'axios'
import { getStaffToken } from './staffToken'

// Cliente HTTP do meufuturo-chat-service (domínio do api-gateway, não do backend-core).
// Instância separada: não herda o interceptor global (que desloga em 401/403) nem o
// Authorization do Sanctum. baseURL e Bearer vêm do token de staff a cada requisição.

const chatClient = axios.create({ withCredentials: false })

const setHeader = (headers, name, value) => {
  if (headers && typeof headers.set === 'function') headers.set(name, value)
  else if (headers) headers[name] = value // eslint-disable-line no-param-reassign
}

const deleteHeader = (headers, name) => {
  if (headers && typeof headers.delete === 'function') headers.delete(name)
  else if (headers) delete headers[name] // eslint-disable-line no-param-reassign
}

chatClient.interceptors.request.use(async config => {
  const token = await getStaffToken()
  const next = config
  next.baseURL = token.apiBaseUrl
  setHeader(next.headers, 'Authorization', `Bearer ${token.token}`)
  // Header do backend-core que o gateway não precisa (evita preflight com header extra).
  deleteHeader(next.headers, 'X-Requested-With')
  return next
})

// Desembrulha `{ success, data, meta }` → `data`.
const unwrap = response => {
  const body = response.data
  if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'success')) {
    return body.data
  }
  return body
}

chatClient.interceptors.response.use(
  unwrap,
  error => {
    const { config, response } = error
    // 401: token de staff expirou/foi revogado → renova UMA vez e repete.
    if (response && response.status === 401 && config && !config.chatRetried) {
      config.chatRetried = true
      return getStaffToken({ forceRefresh: true }).then(() => chatClient.request(config))
    }
    const normalized = error
    // Sem response = falha antes do envio (ex.: token indisponível — status já vem no erro).
    if (response) normalized.status = response.status
    normalized.apiErrors = response && response.data && Array.isArray(response.data.errors)
      ? response.data.errors
      : []
    return Promise.reject(normalized)
  },
)

const encode = value => encodeURIComponent(String(value))

const cleanParams = params => Object.keys(params || {}).reduce((acc, key) => {
  const value = params[key]
  if (value !== undefined && value !== null && value !== '') acc[key] = value
  return acc
}, {})

export const fetchInbox = ({ scope, page = 0, size = 20 }) => chatClient.get('/staff/inbox', { params: { scope, page, size } })

export const fetchConversation = conversationId => chatClient.get(`/staff/conversations/${encode(conversationId)}`)

export const fetchConversationByCustomer = coreCustomerId => chatClient.get(`/staff/customers/${encode(coreCustomerId)}/conversation`)

export const fetchMessages = (conversationId, { beforeSeq, afterSeq, limit } = {}) => chatClient.get(
  `/staff/conversations/${encode(conversationId)}/messages`,
  { params: cleanParams({ beforeSeq, afterSeq, limit }) },
)

export const sendMessage = (conversationId, { clientMessageId, body, replyToMessageId }) => chatClient.post(
  `/staff/conversations/${encode(conversationId)}/messages`,
  cleanParams({ clientMessageId, body, replyToMessageId }),
)

export const markRead = (conversationId, lastReadSeq) => chatClient.post(
  `/staff/conversations/${encode(conversationId)}/read`,
  { lastReadSeq },
)

export const requestWsTicket = () => chatClient.post('/staff/ws-ticket')

// "Direcionar": convidados da conversa (dono convida/remove; convidado sai com o próprio id).
export const fetchParticipants = conversationId => chatClient.get(`/staff/conversations/${encode(conversationId)}/participants`)

export const inviteParticipant = (conversationId, payload) => chatClient.post(
  `/staff/conversations/${encode(conversationId)}/participants`,
  payload,
)

export const removeParticipant = (conversationId, participantId) => chatClient.delete(
  `/staff/conversations/${encode(conversationId)}/participants/${encode(participantId)}`,
)
